// src/components/AuthModal.jsx
import { useState } from "react";
import { X, Eye, EyeOff, Mail, Lock, User } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { OriginalModalShell } from "./ModalShell";

export default function AuthModal({ modal, onClose, showToast }) {
  const { login, register } = useAuth();

  const [tab,         setTab]         = useState(modal);
  const [form,        setForm]        = useState({ username: "", email: "", password: "", confirm: "" });
  const [showPass,    setShowPass]    = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error,       setError]       = useState("");
  const [loading,     setLoading]     = useState(false);

  const switchTab = (t) => { setTab(t); setError(""); setShowPass(false); setShowConfirm(false); };

  const handleSubmit = async () => {
    setError("");
    if (tab === "signup" && form.password !== form.confirm) {
      setError("Passwords do not match."); return;
    }
    setLoading(true);
    try {
      const user = tab === "login"
        ? await login({ email: form.email, password: form.password })
        : await register({ username: form.username, email: form.email, password: form.password });
      showToast(tab === "login" ? `Welcome back, ${user.username}!` : `Welcome to TOA, ${user.username}!`);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const field = (key) => ({
    value: form[key],
    onChange: e => setForm(f => ({ ...f, [key]: e.target.value })),
  });

  const inputStyle = (focused) => ({
    width: "100%", background: "rgba(255,255,255,.04)",
    border: `1px solid ${focused ? "rgba(123,94,167,.6)" : "rgba(123,94,167,.25)"}`,
    padding: "10px 40px 10px 36px", fontSize: 13, color: "#e2d9f3",
    fontFamily: "'Inter',sans-serif", outline: "none",
    transition: ".15s", boxSizing: "border-box",
  });

  const labelStyle = {
    display: "block", fontSize: 11, fontWeight: 600,
    color: "#9b8dbe", letterSpacing: .5, textTransform: "uppercase",
    marginBottom: 5, fontFamily: "'Cinzel',serif",
  };

  return (
    <OriginalModalShell onClose={onClose} maxWidth={420}>
      <div style={{ padding: "24px 32px 28px", position: "relative" }}>
        {/* Close */}
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 20,
          width: 28, height: 28, background: "rgba(255,255,255,.04)",
          border: "1px solid #2a2040", cursor: "pointer",
          color: "#4a4060", display: "flex", alignItems: "center", justifyContent: "center",
          transition: ".15s",
        }}
          onMouseOver={e => { e.currentTarget.style.borderColor = "#7b5ea7"; e.currentTarget.style.color = "#c9a84c"; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = "#2a2040"; e.currentTarget.style.color = "#4a4060"; }}
        >
          <X style={{ width: 11, height: 11 }} />
        </button>

        {/* Title */}
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ fontFamily: "'Cinzel Decorative',serif", fontSize: 13, color: "#c9a84c", marginBottom: 4 }}>
            The Ones Above
          </div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 22, fontWeight: 700, color: "#e2d9f3", marginBottom: 4 }}>
            {tab === "login" ? "Welcome Back" : "Join the Brotherhood"}
          </div>
          <div style={{ fontSize: 12, color: "#6b6080", fontFamily: "'Cinzel',serif" }}>
            {tab === "login" ? "Sign in to your account" : "Create your TOA account"}
          </div>
        </div>

        {/* Tab switcher */}
        <div style={{ display: "flex", border: "1px solid #2a2040", borderRadius: 8, overflow: "hidden", marginBottom: 22 }}>
          {["login", "signup"].map(t => (
            <button key={t} onClick={() => switchTab(t)} style={{
              flex: 1, padding: "9px", fontFamily: "'Cinzel',serif", fontSize: 11, fontWeight: 700,
              letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", border: "none",
              background: tab === t ? "linear-gradient(135deg,#7b5ea7,#5a3d8a)" : "transparent",
              color: tab === t ? "#fff" : "#6b6080",
              transition: ".15s",
            }}>
              {t === "login" ? "Log In" : "Sign Up"}
            </button>
          ))}
        </div>

        {/* Discord OAuth */}
        <button type="button" onClick={() => showToast("Discord OAuth coming soon!")} style={{
          width: "100%", padding: "11px", borderRadius: 8,
          background: "#5865F2", border: "none",
          color: "#fff", fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700,
          letterSpacing: 1, textTransform: "uppercase", cursor: "not-allowed",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 9,
          marginBottom: 18, opacity: 0.5, transition: ".15s",
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>
          Continue with Discord
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <div style={{ flex: 1, height: 1, background: "#2a2040" }} />
          <span style={{ fontSize: 11, color: "#4a4060", fontFamily: "'Cinzel',serif", letterSpacing: .5, whiteSpace: "nowrap" }}>or continue with email</span>
          <div style={{ flex: 1, height: 1, background: "#2a2040" }} />
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 7, padding: "9px 12px", fontSize: 12, color: "#f87171", marginBottom: 12, fontFamily: "'Cinzel',serif" }}>
            {error}
          </div>
        )}

        {/* Username (signup only) */}
        {tab === "signup" && (
          <FieldWrap label="Username" labelStyle={labelStyle}>
            <span style={iconStyle}><User style={{ width: 14, height: 14 }} /></span>
            <input type="text" placeholder="YourTOAName" className="no-right-pad"
              style={inputStyle(false)}
              onFocus={e => e.target.style.borderColor = "rgba(123,94,167,.6)"}
              onBlur={e => e.target.style.borderColor = "rgba(123,94,167,.25)"}
              {...field("username")} />
          </FieldWrap>
        )}

        <FieldWrap label="Email" labelStyle={labelStyle}>
          <span style={iconStyle}><Mail style={{ width: 14, height: 14 }} /></span>
          <input type="email" placeholder="you@email.com" className="no-right-pad"
            style={inputStyle(false)}
            onFocus={e => e.target.style.borderColor = "rgba(123,94,167,.6)"}
            onBlur={e => e.target.style.borderColor = "rgba(123,94,167,.25)"}
            {...field("email")} />
        </FieldWrap>

        <FieldWrap label="Password" labelStyle={labelStyle}>
          <span style={iconStyle}><Lock style={{ width: 14, height: 14 }} /></span>
          <input type={showPass ? "text" : "password"} placeholder="••••••••"
            style={{ ...inputStyle(false), paddingRight: 40 }}
            onFocus={e => e.target.style.borderColor = "rgba(123,94,167,.6)"}
            onBlur={e => e.target.style.borderColor = "rgba(123,94,167,.25)"}
            onKeyDown={e => e.key === "Enter" && tab === "login" && handleSubmit()}
            {...field("password")} />
          <EyeBtn show={showPass} onToggle={() => setShowPass(p => !p)} />
        </FieldWrap>

        {tab === "signup" && (
          <FieldWrap label="Confirm Password" labelStyle={labelStyle}>
            <span style={iconStyle}><Lock style={{ width: 14, height: 14 }} /></span>
            <input type={showConfirm ? "text" : "password"} placeholder="••••••••"
              style={{ ...inputStyle(false), paddingRight: 40 }}
              onFocus={e => e.target.style.borderColor = "rgba(123,94,167,.6)"}
              onBlur={e => e.target.style.borderColor = "rgba(123,94,167,.25)"}
              {...field("confirm")} />
            <EyeBtn show={showConfirm} onToggle={() => setShowConfirm(p => !p)} />
          </FieldWrap>
        )}

        {/* Submit */}
        <button onClick={handleSubmit} disabled={loading} style={{
          width: "100%", padding: "12px", borderRadius: 8,
          background: "linear-gradient(135deg,#7b5ea7,#5a3d8a)",
          border: "none", color: "#fff", fontFamily: "'Cinzel',serif",
          fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase",
          cursor: loading ? "not-allowed" : "pointer", transition: ".15s", marginTop: 4,
          boxShadow: "0 0 20px rgba(123,94,167,.3)",
          opacity: loading ? 0.6 : 1,
        }}
          onMouseOver={e => { if (!loading) e.currentTarget.style.boxShadow = "0 0 30px rgba(123,94,167,.5)"; e.currentTarget.style.transform = loading ? "" : "translateY(-1px)"; }}
          onMouseOut={e => { e.currentTarget.style.boxShadow = "0 0 20px rgba(123,94,167,.3)"; e.currentTarget.style.transform = ""; }}
        >
          {loading ? "Please wait..." : tab === "login" ? "Sign In" : "Create Account"}
        </button>

        {/* Footer */}
        <p style={{ textAlign: "center", fontSize: 11, color: "#4a4060", marginTop: 14, lineHeight: 1.6, fontFamily: "'Cinzel',serif" }}>
          {tab === "signup"
            ? <>By creating an account you agree to our <a href="#" style={{ color: "#7b5ea7", textDecoration: "none" }}>Terms</a> and <a href="#" style={{ color: "#7b5ea7", textDecoration: "none" }}>Privacy Policy</a>.</>
            : <>Forgot your password? <a href="#" style={{ color: "#7b5ea7", textDecoration: "none" }}>Reset it here</a>.</>
          }
        </p>
      </div>
    </OriginalModalShell>
  );
}

/* ── Small helpers ───────────────────────────────────────────────────────  */
const iconStyle = {
  position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
  color: "#4a4060", pointerEvents: "none", display: "flex", alignItems: "center", zIndex: 1,
};

function FieldWrap({ label, labelStyle, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {children}
      </div>
    </div>
  );
}

function EyeBtn({ show, onToggle }) {
  return (
    <button type="button" onClick={onToggle} style={{
      position: "absolute", right: 0, top: 0, bottom: 0, width: 38,
      background: "none", border: "none", borderLeft: "1px solid rgba(123,94,167,.2)",
      color: "#4a4060", cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", transition: ".15s", zIndex: 2,
    }}
      onMouseOver={e => e.currentTarget.style.color = "#9b7fd4"}
      onMouseOut={e => e.currentTarget.style.color = "#4a4060"}
    >
      {show ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
    </button>
  );
}